import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Combobox,
  FileButton,
  Flex,
  Group,
  Input,
  InputBase,
  Menu,
  Paper,
  Popover,
  ScrollArea,
  SegmentedControl,
  Stack,
  Text,
  TextInput,
  Tooltip,
} from "@mantine/core";
import {
  IconAdjustmentsHorizontal,
  IconArrowBackUp,
  IconPhoto,
  IconPlus,
  IconSearch,
  IconTrash,
  IconUpload,
} from "@tabler/icons-react";
import {
  AppWindowIcon,
  ArmchairIcon,
  BadgePercentIcon,
  BriefcaseIcon,
  Building2Icon,
  CameraIcon,
  CctvIcon,
  ClockIcon,
  CpuIcon,
  CreditCardIcon,
  FanIcon,
  FileTextIcon,
  Gamepad2Icon,
  GiftIcon,
  GpuIcon,
  GraduationCapIcon,
  HardDriveIcon,
  HeadphonesIcon,
  HeadsetIcon,
  HeartIcon,
  HospitalIcon,
  HouseIcon,
  KeyboardIcon,
  LandmarkIcon,
  LaptopIcon,
  LeafIcon,
  LucideIcon,
  MicIcon,
  MonitorIcon,
  MouseIcon,
  PackageIcon,
  PaletteIcon,
  PcCaseIcon,
  PrinterIcon,
  RulerIcon,
  SettingsIcon,
  ShieldCheckIcon,
  SparklesIcon,
  SpeakerIcon,
  StarIcon,
  StoreIcon,
  TabletIcon,
  TruckIcon,
  UserCheckIcon,
  UsersIcon,
  VideoIcon,
  WebcamIcon,
  WifiIcon,
  WrenchIcon,
} from "lucide-react";
import React, { Dispatch, SetStateAction, useCallback, useEffect, useRef, useState } from "react";
import { useHomeBlockStore } from "../../../store/useHomeBlockStore";
import { toSearchKey } from "../CategoryTreeSelect";
import { IconLabel, IconValue } from "../homeBlockConfig";
import {
  CropImage,
  DragHandle,
  reorderList,
  SortableArea,
  useSortableItem,
  ZoomableImage,
} from "../HomeBlockShared";
import { ACCENT, designImage, HomeBlock } from "../homeBlockData";
import classes from "../homeBlock.module.css";
import { previewImage } from "../pendingImages";

export type Crop = { x: number; y: number; w: number; h: number };

export const newId = () => `hb-${Math.random().toString(36).slice(2, 9)}`;

// Ảnh chọn từ máy chỉ xem trước (previewImage), bấm Lưu block mới tải lên CDN
const IMAGE_ACCEPT = "image/png,image/jpeg,image/webp";

// Cấu hình chi tiết của block là một object JSON (cột CONFIG, kiểu ở homeBlockConfig.ts).
// Giữ trong store để tab "Dữ liệu lưu" và nút Lưu dùng chung; lần đầu mở block thì lấy từ init().
export const useBlockConfig = <T,>(block: HomeBlock, init: () => T) => {
  const initConfig = useHomeBlockStore((s) => s.initConfig);
  const updateConfig = useHomeBlockStore((s) => s.updateConfig);
  const [fallback] = useState(() => (block.config === undefined ? init() : undefined));

  useEffect(() => {
    if (block.config === undefined && fallback !== undefined) initConfig(block.id, fallback);
  }, [block.id, block.config, fallback, initConfig]);

  const setConfig: Dispatch<SetStateAction<T>> = useCallback(
    (action) =>
      updateConfig(block.id, (stored) => {
        const prev = (stored === undefined ? fallback : stored) as T;
        return typeof action === "function" ? (action as (p: T) => T)(prev) : action;
      }),
    [block.id, fallback, updateConfig]
  );

  return [(block.config ?? fallback) as T, setConfig] as const;
};

// Áp một giá trị kiểu setState (giá trị hoặc hàm) lên giá trị cũ, dùng khi cập nhật danh sách lồng trong config
export const applyAction = <T,>(action: SetStateAction<T>, prev: T): T =>
  typeof action === "function" ? (action as (p: T) => T)(prev) : action;

// Setter cho một nhánh của config, dùng được như setState (kể cả dạng hàm) cho EditableList
export const fieldSetter =
  <T, K extends keyof T>(set: Dispatch<SetStateAction<T>>, key: K): Dispatch<SetStateAction<T[K]>> =>
  (action) =>
    set((c) => ({
      ...c,
      [key]: typeof action === "function" ? (action as (p: T[K]) => T[K])(c[key]) : action,
    }));

type EditorLayoutProps = {
  block: HomeBlock;
  children: React.ReactNode;
  note?: string;
};

// Khung chung của trình sửa: cột form bên trái, ảnh thiết kế bám theo khi cuộn bên phải
export const EditorLayout = ({
  block,
  children,
  note = "Ảnh thiết kế gốc để đối chiếu, không đổi theo nội dung đang sửa.",
}: EditorLayoutProps) => (
  <Flex wrap="wrap" align="flex-start" gap={16}>
    <Stack gap={16} style={{ flex: "999 1 640px", minWidth: 0 }}>
      {children}
    </Stack>
    <Box
      component="aside"
      aria-label="Ảnh thiết kế"
      className={classes.detailSticky}
      style={{ flex: "1 1 360px", maxWidth: "100%", minWidth: 0 }}
    >
      <Paper withBorder radius="md">
        <Text
          component="h2"
          fw={700}
          fz={15}
          m={0}
          px={16}
          py={14}
          style={{ borderBottom: "1px solid var(--hb-border)" }}
        >
          Ảnh thiết kế
        </Text>
        <Box p={16} bg="var(--hb-surface-2)">
          <ZoomableImage
            imageId={block.thumbId}
            alt={`Ảnh thiết kế ${block.name}`}
          />
        </Box>
        <Text fz={12} px={16} pt={10} pb={14} className={classes.muted}>
          {note}
        </Text>
      </Paper>
    </Box>
  </Flex>
);

type ImagePickerProps = {
  label?: string;
  // Ảnh đang dùng (URL). Chưa có thì hiện ảnh mẫu cắt từ thiết kế nếu truyền imageId + crop
  src?: string | null;
  imageId?: string;
  crop?: Crop;
  onPick: (url: string) => void;
  onClear?: () => void;
  height?: number;
  maxWidth?: number;
};

// Ô ảnh lớn: hiện ảnh đang dùng (hoặc ảnh mẫu từ thiết kế), chưa có thì là ô kéo thả
export const ImagePicker = ({
  label,
  src,
  imageId,
  crop,
  onPick,
  onClear,
  height = 150,
  maxWidth = 480,
}: ImagePickerProps) => {
  const hasDesign = !!(imageId && crop) && !src;

  // Bấm thẳng vào ảnh đang hiện để chọn ảnh khác
  const changeOnClick = (face: React.ReactNode) => (
    <FileButton accept={IMAGE_ACCEPT} onChange={(file) => file && onPick(previewImage(file))}>
      {(props) => (
        <Tooltip label="Bấm để đổi ảnh" openDelay={300} withinPortal>
          <button
            {...props}
            type="button"
            className={classes.thumbButton}
            style={{ width: "100%" }}
            aria-label={`${label ?? "Ảnh"}: bấm để đổi ảnh`}
          >
            {face}
          </button>
        </Tooltip>
      )}
    </FileButton>
  );

  return (
    <Box maw={maxWidth}>
      {label && (
        <Text fz={13} fw={600} mb={6}>
          {label}
        </Text>
      )}
      {src ? (
        changeOnClick(
          <img src={src} alt={label ?? "Ảnh hiện tại"} className={classes.pickedImg} style={{ height }} />
        )
      ) : hasDesign ? (
        changeOnClick(
          <Box pos="relative">
            <CropImage
              src={designImage(imageId as string, 1600)}
              alt={label ?? "Ảnh mẫu"}
              {...(crop as Crop)}
            />
            <Badge pos="absolute" top={6} left={6} size="xs" color="gray" variant="filled" tt="none">
              Ảnh mẫu từ thiết kế
            </Badge>
          </Box>
        )
      ) : (
        <FileButton accept={IMAGE_ACCEPT} onChange={(file) => file && onPick(previewImage(file))}>
          {(props) => (
            <button {...props} type="button" className={classes.dropSlot} style={{ height }}>
              <IconUpload size={22} aria-hidden />
              <span>Kéo ảnh vào đây hoặc chọn từ Media</span>
            </button>
          )}
        </FileButton>
      )}
      {(hasDesign || src) && (
        <Group gap={8} mt={8}>
          <FileButton accept={IMAGE_ACCEPT} onChange={(file) => file && onPick(previewImage(file))}>
            {(props) => (
              <Button {...props} size="xs" variant="default">
                Đổi ảnh
              </Button>
            )}
          </FileButton>
          {onClear && (
            <Button size="xs" variant="subtle" color="red" onClick={onClear}>
              Gỡ ảnh
            </Button>
          )}
        </Group>
      )}
    </Box>
  );
};

type ThumbPickerProps = {
  label: string;
  // Ảnh riêng đã tải lên (URL)
  src?: string | null;
  // Ảnh mặc định của nguồn khi chưa có ảnh riêng, ví dụ ảnh của danh mục
  fallbackSrc?: string | null;
  fallbackNote?: string;
  // Ảnh mẫu cắt từ thiết kế, chỉ hiện khi chưa có ảnh nào
  imageId?: string;
  crop?: Crop;
  onPick: (url: string) => void;
  // Bỏ ảnh riêng để quay về ảnh mặc định
  onReset?: () => void;
  resetLabel?: string;
  width?: number;
  height?: number;
};

// Ô ảnh nhỏ trong một dòng danh sách. Bấm để tải ảnh khác; khi đang dùng ảnh riêng thì có menu quay về ảnh mặc định.
export const ThumbPicker = ({
  label,
  src,
  fallbackSrc,
  fallbackNote = "Ảnh mặc định",
  imageId,
  crop,
  onPick,
  onReset,
  resetLabel = "Dùng ảnh mặc định",
  width = 56,
  height = 40,
}: ThumbPickerProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const openFile = () => inputRef.current?.click();
  // Ảnh không tải được (link hỏng, file không còn trên CDN): hiện ô trống thay cho ảnh vỡ
  const [brokenSrc, setBrokenSrc] = useState<string | null>(null);
  const wanted = src || fallbackSrc;
  const broken = !!wanted && wanted === brokenSrc;
  const shown = broken ? null : wanted;
  const hasDesign = !shown && !broken && !!(imageId && crop);
  const source = broken
    ? "Ảnh không tải được"
    : src
      ? "Ảnh riêng"
      : fallbackSrc
        ? fallbackNote
        : hasDesign
          ? "Ảnh mẫu từ thiết kế"
          : "Chưa có ảnh";
  const withMenu = !!(src && onReset);

  const face = shown ? (
    <img
      src={shown}
      alt=""
      className={classes.thumbImg}
      style={{ width, height }}
      onError={() => setBrokenSrc(shown)}
    />
  ) : hasDesign ? (
    <CropImage src={designImage(imageId as string, 1600)} alt="" {...(crop as Crop)} width={width} radius={4} />
  ) : (
    <IconPhoto size={18} />
  );

  const trigger = (
    <button
      type="button"
      className={shown || hasDesign ? classes.thumbButton : classes.dropSlot}
      aria-label={`Ảnh ${label}: ${source.toLowerCase()}. ${withMenu ? "Mở tuỳ chọn ảnh" : "Bấm để tải ảnh khác"}`}
      onClick={withMenu ? undefined : openFile}
      style={shown || hasDesign ? { width } : { width, height, padding: 0 }}
    >
      {face}
      {src && onReset && <span className={classes.thumbMark} aria-hidden />}
    </button>
  );

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept={IMAGE_ACCEPT}
        hidden
        onChange={(e) => {
          const file = e.currentTarget.files?.[0];
          if (file) onPick(previewImage(file));
          e.currentTarget.value = "";
        }}
      />
      {withMenu ? (
        <Menu position="bottom-start" shadow="md" withinPortal>
          <Tooltip label={`${source}. Bấm để đổi`} openDelay={300} withinPortal>
            <Menu.Target>{trigger}</Menu.Target>
          </Tooltip>
          <Menu.Dropdown>
            <Menu.Label>{label}</Menu.Label>
            <Menu.Item leftSection={<IconUpload size={14} />} onClick={openFile}>
              Tải ảnh khác
            </Menu.Item>
            <Menu.Item leftSection={<IconArrowBackUp size={14} />} onClick={onReset}>
              {resetLabel}
            </Menu.Item>
          </Menu.Dropdown>
        </Menu>
      ) : (
        <Tooltip label={`${source}. Bấm để tải ảnh khác`} openDelay={300} withinPortal>
          {trigger}
        </Tooltip>
      )}
    </>
  );
};

type ListColumn = { label: string; width: string };

type EditableListProps<T extends { id: string }> = {
  items: T[];
  onChange: React.Dispatch<React.SetStateAction<T[]>>;
  columns: ListColumn[];
  renderCells: (
    item: T,
    update: (patch: Partial<T>) => void,
    index: number
  ) => React.ReactNode;
  itemLabel: (item: T) => string;
  newItem?: () => T;
  addLabel?: string;
  max?: number;
  minWidth?: number;
  // Phần cấu hình thêm của từng dòng, mở bằng nút ở cuối dòng
  renderDetails?: (item: T, update: (patch: Partial<T>) => void) => React.ReactNode;
  detailsLabel?: string;
};

type ListRowProps = {
  id: string;
  label: string;
  template: string;
  onRemove: () => void;
  children: React.ReactNode;
  details?: React.ReactNode;
  detailsLabel?: string;
  open?: boolean;
  onToggle?: () => void;
};

const ListRow = ({
  id,
  label,
  template,
  onRemove,
  children,
  details,
  detailsLabel,
  open,
  onToggle,
}: ListRowProps) => {
  const { setNodeRef, style, handleProps } = useSortableItem(id);
  return (
    <div
      ref={setNodeRef}
      className={classes.listRow}
      style={{
        ...style,
        gridTemplateColumns: template,
        background: open ? "var(--hb-surface-2)" : "#fff",
      }}
    >
      <DragHandle label={`Kéo để đổi thứ tự ${label}`} handleProps={handleProps} size={16} />
      {children}
      {onToggle && (
        <ActionIcon
          variant={open ? "light" : "subtle"}
          color={open ? ACCENT : "gray"}
          aria-label={`${detailsLabel ?? "Tuỳ chỉnh"} ${label}`}
          aria-expanded={open}
          title={detailsLabel}
          onClick={onToggle}
        >
          <IconAdjustmentsHorizontal size={16} />
        </ActionIcon>
      )}
      <ActionIcon variant="subtle" color="gray" aria-label={`Xoá ${label}`} onClick={onRemove}>
        <IconTrash size={16} />
      </ActionIcon>
      {open && details && (
        <Box style={{ gridColumn: "1 / -1" }} pl={30} pr={8} pb={6}>
          {details}
        </Box>
      )}
    </div>
  );
};

// Danh sách dòng kéo thả được, mỗi dòng là một lưới ô nhập; có thêm, xoá, giới hạn số mục
export const EditableList = <T extends { id: string }>({
  items,
  onChange,
  columns,
  renderCells,
  itemLabel,
  newItem,
  addLabel = "Thêm mục",
  max,
  minWidth = 560,
  renderDetails,
  detailsLabel = "Tuỳ chỉnh hiển thị",
}: EditableListProps<T>) => {
  const [openId, setOpenId] = useState<string | null>(null);
  const toggleWidth = renderDetails ? " 30px" : "";
  const template = `20px ${columns.map((c) => c.width).join(" ")}${toggleWidth} 30px`;
  const full = max !== undefined && items.length >= max;

  const update = (id: string, patch: Partial<T>) =>
    onChange((prev) => prev.map((it) => (it.id === id ? { ...it, ...patch } : it)));

  return (
    <Box>
      <Box style={{ overflowX: "auto" }}>
        <Box miw={minWidth}>
          <div
            className={`${classes.listRow} ${classes.listRowHeader}`}
            style={{ gridTemplateColumns: template }}
          >
            <span />
            {columns.map((c) => (
              <span key={c.label}>{c.label}</span>
            ))}
            {renderDetails && <span />}
            <span />
          </div>
          <SortableArea
            ids={items.map((it) => it.id)}
            onReorder={(from, to) => onChange((prev) => reorderList(prev, from, to))}
          >
            {items.map((item, index) => (
              <ListRow
                key={item.id}
                id={item.id}
                label={itemLabel(item) || "mục chưa đặt tên"}
                template={template}
                onRemove={() => onChange((prev) => prev.filter((it) => it.id !== item.id))}
                details={
                  renderDetails ? renderDetails(item, (patch) => update(item.id, patch)) : undefined
                }
                detailsLabel={detailsLabel}
                open={openId === item.id}
                onToggle={
                  renderDetails
                    ? () => setOpenId((cur) => (cur === item.id ? null : item.id))
                    : undefined
                }
              >
                {renderCells(item, (patch) => update(item.id, patch), index)}
              </ListRow>
            ))}
          </SortableArea>
        </Box>
      </Box>
      {items.length === 0 && (
        <Text fz={13} py={10} className={classes.muted}>
          Chưa có mục nào.
        </Text>
      )}
      {newItem && (
        <Group mt={10} gap={12}>
          {!full && (
            <Button
              size="xs"
              variant="default"
              leftSection={<IconPlus size={14} />}
              onClick={() => onChange((prev) => [...prev, newItem()])}
            >
              {addLabel}
            </Button>
          )}
          {max !== undefined && (
            <Text fz={12} className={classes.muted}>
              {full
                ? `Đủ ${max} mục. Xoá bớt một mục để thêm mục mới.`
                : `${items.length} / ${max} mục`}
            </Text>
          )}
        </Group>
      )}
    </Box>
  );
};

type AccordionHead = {
  title: string;
  subtitle?: string;
  thumb?: React.ReactNode;
  // Ô ảnh bấm được (ThumbPicker): nằm ngoài nút mở/đóng thẻ để bấm vào là chọn ảnh
  thumbPicker?: React.ReactNode;
  badge?: string;
};

type AccordionListProps<T extends { id: string }> = {
  items: T[];
  onChange: React.Dispatch<React.SetStateAction<T[]>>;
  renderHead: (item: T, index: number, update: (patch: Partial<T>) => void) => AccordionHead;
  renderBody: (
    item: T,
    update: (patch: Partial<T>) => void,
    index: number
  ) => React.ReactNode;
  itemLabel: (item: T) => string;
  newItem?: () => T;
  addLabel?: string;
  max?: number;
};

type AccordionItemProps = {
  id: string;
  label: string;
  head: AccordionHead;
  open: boolean;
  onToggle: () => void;
  onRemove: () => void;
  children: React.ReactNode;
};

const AccordionItem = ({
  id,
  label,
  head,
  open,
  onToggle,
  onRemove,
  children,
}: AccordionItemProps) => {
  const { setNodeRef, style, handleProps } = useSortableItem(id);
  return (
    <div ref={setNodeRef} className={`${classes.tile} ${open ? classes.tileOpen : ""}`} style={style}>
      <Group gap={12} wrap="nowrap" px={10} py={8}>
        <DragHandle label={`Kéo để đổi thứ tự ${label}`} handleProps={handleProps} size={16} />
        {head.thumbPicker}
        <button
          type="button"
          className={classes.tileHead}
          aria-expanded={open}
          onClick={onToggle}
          style={head.thumbPicker ? { gridTemplateColumns: "minmax(0, 1fr) auto" } : undefined}
        >
          {!head.thumbPicker && (head.thumb ?? (
            <Flex
              align="center"
              justify="center"
              w={64}
              h={48}
              style={{
                border: "1px dashed var(--hb-border-strong)",
                borderRadius: 4,
                color: "var(--hb-muted)",
                background: "var(--hb-surface-2)",
              }}
            >
              <IconPhoto size={18} aria-hidden />
            </Flex>
          ))}
          <Stack gap={3} miw={0}>
            <Text fz={14} fw={600} lh={1.3} truncate>
              {head.title || "Chưa có tiêu đề"}
            </Text>
            {head.subtitle && (
              <Text fz={12} lh={1.3} truncate className={classes.muted}>
                {head.subtitle}
              </Text>
            )}
          </Stack>
          {head.badge ? (
            <Badge variant="light" color="gray" radius="xl" tt="none" fw={600}>
              {head.badge}
            </Badge>
          ) : (
            <span />
          )}
        </button>
        <ActionIcon variant="subtle" color="gray" aria-label={`Xoá ${label}`} onClick={onRemove}>
          <IconTrash size={16} />
        </ActionIcon>
      </Group>
      {open && (
        <Stack gap={12} pt={4} pb={14} pr={12} pl={42}>
          {children}
        </Stack>
      )}
    </div>
  );
};

// Danh sách thẻ mở/đóng, kéo thả được; dùng cho mục có nhiều trường (thẻ, ô, video...)
export const AccordionList = <T extends { id: string }>({
  items,
  onChange,
  renderHead,
  renderBody,
  itemLabel,
  newItem,
  addLabel = "Thêm mục",
  max,
}: AccordionListProps<T>) => {
  const [openId, setOpenId] = useState<string | null>(items[0]?.id ?? null);
  const full = max !== undefined && items.length >= max;

  const update = (id: string, patch: Partial<T>) =>
    onChange((prev) => prev.map((it) => (it.id === id ? { ...it, ...patch } : it)));

  return (
    <Stack gap={8}>
      <SortableArea
        ids={items.map((it) => it.id)}
        onReorder={(from, to) => onChange((prev) => reorderList(prev, from, to))}
      >
        {items.map((item, index) => (
          <AccordionItem
            key={item.id}
            id={item.id}
            label={itemLabel(item) || "mục chưa đặt tên"}
            head={renderHead(item, index, (patch) => update(item.id, patch))}
            open={openId === item.id}
            onToggle={() => setOpenId(openId === item.id ? null : item.id)}
            onRemove={() => onChange((prev) => prev.filter((it) => it.id !== item.id))}
          >
            {renderBody(item, (patch) => update(item.id, patch), index)}
          </AccordionItem>
        ))}
      </SortableArea>
      {items.length === 0 && (
        <Text fz={13} className={classes.muted}>
          Chưa có mục nào.
        </Text>
      )}
      {newItem && (
        <Group gap={12}>
          {!full && (
            <Button
              size="xs"
              variant="default"
              leftSection={<IconPlus size={14} />}
              onClick={() => {
                const created = newItem();
                onChange((prev) => [...prev, created]);
                setOpenId(created.id);
              }}
            >
              {addLabel}
            </Button>
          )}
          {max !== undefined && (
            <Text fz={12} className={classes.muted}>
              {full
                ? `Đủ ${max} mục. Xoá bớt một mục để thêm mục mới.`
                : `${items.length} / ${max} mục`}
            </Text>
          )}
        </Group>
      )}
    </Stack>
  );
};

type IconChoice = { name: string; label: string; Icon: LucideIcon };

// Icon nội dung lấy từ Lucide (https://lucide.dev/icons) giống website.
// JSON lưu tên Lucide (ví dụ "truck"); nhãn tiếng Việt chỉ để tìm và hiển thị trong trình sửa.
export const ICON_CHOICES: IconChoice[] = [
  { name: "laptop", label: "Laptop", Icon: LaptopIcon },
  { name: "gamepad-2", label: "Tay cầm", Icon: Gamepad2Icon },
  { name: "pc-case", label: "Máy tính", Icon: PcCaseIcon },
  { name: "tablet", label: "Máy tính bảng", Icon: TabletIcon },
  { name: "printer", label: "Máy in", Icon: PrinterIcon },
  { name: "monitor", label: "Màn hình", Icon: MonitorIcon },
  { name: "cctv", label: "Camera an ninh", Icon: CctvIcon },
  { name: "cpu", label: "Chip", Icon: CpuIcon },
  { name: "gpu", label: "Card đồ hoạ", Icon: GpuIcon },
  { name: "hard-drive", label: "Ổ cứng", Icon: HardDriveIcon },
  { name: "keyboard", label: "Bàn phím", Icon: KeyboardIcon },
  { name: "mouse", label: "Chuột", Icon: MouseIcon },
  { name: "webcam", label: "Webcam", Icon: WebcamIcon },
  { name: "speaker", label: "Loa", Icon: SpeakerIcon },
  { name: "wifi", label: "Wifi", Icon: WifiIcon },
  { name: "app-window", label: "Phần mềm", Icon: AppWindowIcon },
  { name: "armchair", label: "Ghế", Icon: ArmchairIcon },
  { name: "fan", label: "Quạt", Icon: FanIcon },
  { name: "graduation-cap", label: "Mũ tốt nghiệp", Icon: GraduationCapIcon },
  { name: "briefcase", label: "Va li", Icon: BriefcaseIcon },
  { name: "palette", label: "Bảng màu", Icon: PaletteIcon },
  { name: "video", label: "Video", Icon: VideoIcon },
  { name: "ruler", label: "Thước kẻ", Icon: RulerIcon },
  { name: "sparkles", label: "AI", Icon: SparklesIcon },
  { name: "mic", label: "Micro", Icon: MicIcon },
  { name: "landmark", label: "Ngân hàng", Icon: LandmarkIcon },
  { name: "building-2", label: "Toà nhà", Icon: Building2Icon },
  { name: "hospital", label: "Bệnh viện", Icon: HospitalIcon },
  { name: "badge-percent", label: "Phần trăm", Icon: BadgePercentIcon },
  { name: "user-check", label: "Người hỗ trợ", Icon: UserCheckIcon },
  { name: "file-text", label: "Tài liệu", Icon: FileTextIcon },
  { name: "credit-card", label: "Thẻ thanh toán", Icon: CreditCardIcon },
  { name: "truck", label: "Xe giao hàng", Icon: TruckIcon },
  { name: "shield-check", label: "Khiên bảo vệ", Icon: ShieldCheckIcon },
  { name: "wrench", label: "Cờ lê", Icon: WrenchIcon },
  { name: "clock", label: "Đồng hồ", Icon: ClockIcon },
  { name: "headphones", label: "Tai nghe", Icon: HeadphonesIcon },
  { name: "headset", label: "Tai nghe hỗ trợ", Icon: HeadsetIcon },
  { name: "star", label: "Ngôi sao", Icon: StarIcon },
  { name: "gift", label: "Hộp quà", Icon: GiftIcon },
  { name: "package", label: "Hộp hàng", Icon: PackageIcon },
  { name: "store", label: "Cửa hàng", Icon: StoreIcon },
  { name: "heart", label: "Tim", Icon: HeartIcon },
  { name: "house", label: "Ngôi nhà", Icon: HouseIcon },
  { name: "leaf", label: "Tiết kiệm", Icon: LeafIcon },
  { name: "settings", label: "Cài đặt", Icon: SettingsIcon },
  { name: "camera", label: "Máy ảnh", Icon: CameraIcon },
  { name: "users", label: "Người dùng", Icon: UsersIcon },
];

const ICON_BY_NAME = new Map(ICON_CHOICES.map((choice) => [choice.name, choice]));
const ALL_ICON_NAMES = ICON_CHOICES.map((choice) => choice.name);

export const iconLabel = (name: string) => ICON_BY_NAME.get(name)?.label ?? name;

export const lucideIcon = (name: string): IconValue => ({ type: "lucide", name });

const ICON_IMAGE_ACCEPT = "image/png,image/svg+xml,image/webp";

type IconMode = "lucide" | "image";

type IconPickerProps = {
  value: IconValue;
  onChange: (value: IconValue) => void;
  label?: string;
  ariaLabel?: string;
  // Giới hạn danh sách tên Lucide được chọn; mặc định toàn bộ ICON_CHOICES
  options?: string[];
  clearable?: boolean;
};

// Ô chọn icon, hai cách: chọn icon Lucide trong bảng lưới (tìm được không dấu), hoặc tải ảnh icon lên
export const IconPicker = ({
  value,
  onChange,
  label,
  ariaLabel,
  options = ALL_ICON_NAMES,
  clearable = true,
}: IconPickerProps) => {
  const [opened, setOpened] = useState(false);
  const [mode, setMode] = useState<IconMode>("lucide");
  const [search, setSearch] = useState("");
  const query = toSearchKey(search.trim());
  const shown = query
    ? options.filter((name) => toSearchKey(`${iconLabel(name)} ${name}`).includes(query))
    : options;
  const lucideName = value?.type === "lucide" ? value.name : null;
  const imageUrl = value?.type === "image" ? value.url : null;
  const Current = lucideName ? ICON_BY_NAME.get(lucideName)?.Icon : undefined;
  const currentText = lucideName ? iconLabel(lucideName) : imageUrl ? "Ảnh tải lên" : null;

  const toggle = (next: boolean) => {
    setOpened(next);
    if (next) setMode(value?.type === "image" ? "image" : "lucide");
    else setSearch("");
  };

  const pick = (next: IconValue) => {
    onChange(next);
    toggle(false);
  };

  return (
    <Popover
      opened={opened}
      onChange={toggle}
      width={372}
      position="bottom-start"
      shadow="md"
      trapFocus
      returnFocus
    >
      <Popover.Target>
        <InputBase
          component="button"
          type="button"
          pointer
          label={label}
          aria-label={label ? undefined : ariaLabel}
          aria-haspopup="dialog"
          aria-expanded={opened}
          onClick={() => toggle(!opened)}
          leftSection={
            Current ? (
              <Current size={18} strokeWidth={1.75} />
            ) : imageUrl ? (
              <img src={imageUrl} alt="" className={classes.iconImageSmall} />
            ) : undefined
          }
          // Nút tải ảnh ngay trên ô để thấy được lựa chọn thứ hai mà không cần mở bảng
          rightSection={
            <Group gap={2} wrap="nowrap">
              <FileButton
                accept={ICON_IMAGE_ACCEPT}
                onChange={(file) => file && pick({ type: "image", url: previewImage(file) })}
              >
                {(props) => (
                  <Tooltip label="Tải ảnh làm icon" openDelay={300} withinPortal>
                    <ActionIcon
                      {...props}
                      size="sm"
                      variant="subtle"
                      color="gray"
                      aria-label={`Tải ảnh làm icon${label ? ` ${label}` : ariaLabel ? ` ${ariaLabel}` : ""}`}
                      style={{ pointerEvents: "auto" }}
                    >
                      <IconUpload size={14} />
                    </ActionIcon>
                  </Tooltip>
                )}
              </FileButton>
              <Combobox.Chevron />
            </Group>
          }
          rightSectionWidth={58}
          rightSectionPointerEvents="none"
        >
          {currentText ? (
            <span className={classes.iconValue}>{currentText}</span>
          ) : (
            <Input.Placeholder>Chọn icon hoặc tải ảnh</Input.Placeholder>
          )}
        </InputBase>
      </Popover.Target>
      <Popover.Dropdown className={classes.iconPanel} p={10}>
        <SegmentedControl
          fullWidth
          size="xs"
          mb={10}
          aria-label="Cách chọn icon"
          value={mode}
          onChange={(next) => setMode(next as IconMode)}
          data={[
            { value: "lucide", label: "Chọn icon" },
            { value: "image", label: "Tải ảnh lên" },
          ]}
        />
        {mode === "lucide" ? (
          <>
            <TextInput
              data-autofocus
              size="xs"
              mb={8}
              aria-label="Tìm icon"
              placeholder="Tìm icon, ví dụ: giao hang, truck"
              leftSection={<IconSearch size={14} />}
              value={search}
              onChange={(e) => setSearch(e.currentTarget.value)}
              onKeyDown={(e) => {
                // Enter chọn luôn icon đầu tiên khớp
                if (e.key === "Enter" && shown.length > 0) {
                  e.preventDefault();
                  pick(lucideIcon(shown[0]));
                }
              }}
            />
            {shown.length === 0 ? (
              <Text fz={13} ta="center" py={20} className={classes.muted}>
                Không có icon nào khớp "{search.trim()}"
              </Text>
            ) : (
              <ScrollArea.Autosize mah={264} type="auto" offsetScrollbars>
                <div className={classes.iconGrid}>
                  {shown.map((name) => {
                    const Glyph = ICON_BY_NAME.get(name)?.Icon;
                    const active = name === lucideName;
                    return (
                      <button
                        key={name}
                        type="button"
                        title={`${iconLabel(name)} (${name})`}
                        aria-pressed={active}
                        className={
                          active ? `${classes.iconTile} ${classes.iconTileActive}` : classes.iconTile
                        }
                        onClick={() => pick(lucideIcon(name))}
                      >
                        {Glyph ? (
                          <Glyph size={22} strokeWidth={1.75} />
                        ) : (
                          <IconPhoto size={22} stroke={1.6} />
                        )}
                        <span className={classes.iconTileName}>{iconLabel(name)}</span>
                      </button>
                    );
                  })}
                </div>
              </ScrollArea.Autosize>
            )}
          </>
        ) : (
          <Group gap={12} wrap="nowrap" align="center" py={4}>
            <span className={classes.iconImageBox}>
              {imageUrl ? <img src={imageUrl} alt="Icon đang dùng" /> : <IconPhoto size={22} />}
            </span>
            <Stack gap={6} miw={0}>
              <FileButton
                accept={ICON_IMAGE_ACCEPT}
                onChange={(file) => file && pick({ type: "image", url: previewImage(file) })}
              >
                {(props) => (
                  <Button {...props} size="xs" variant="default" leftSection={<IconUpload size={14} />}>
                    {imageUrl ? "Đổi ảnh icon" : "Chọn ảnh icon"}
                  </Button>
                )}
              </FileButton>
              <Text fz={12} className={classes.muted}>
                PNG, SVG hoặc WebP nền trong suốt, ảnh vuông từ 64×64 px.
              </Text>
            </Stack>
          </Group>
        )}
        {clearable && value && (
          <Group justify="space-between" mt={8} pt={8} className={classes.iconPanelFoot}>
            <Text fz={12} className={classes.muted}>
              Đang chọn: {lucideName ? `${iconLabel(lucideName)} (${lucideName})` : "ảnh tải lên"}
            </Text>
            <Button size="compact-xs" variant="subtle" color="gray" onClick={() => pick(null)}>
              Bỏ icon
            </Button>
          </Group>
        )}
      </Popover.Dropdown>
    </Popover>
  );
};

export const iconLabelItem = (icon: string, label: string): IconLabel => ({
  id: newId(),
  icon: lucideIcon(icon),
  label,
});

type IconLabelListProps = {
  label: string;
  items: IconLabel[];
  onChange: Dispatch<SetStateAction<IconLabel[]>>;
  max: number;
  addLabel: string;
  textLabel?: string;
};

// Danh sách dòng chữ có icon (chọn icon Lucide hoặc tải ảnh): cam kết, gạch đầu dòng, các bước...
export const IconLabelList = ({
  label,
  items,
  onChange,
  max,
  addLabel,
  textLabel = "Nội dung",
}: IconLabelListProps) => (
  <Box>
    <Text fz={14} fw={500} mb={6}>
      {label} (tối đa {max})
    </Text>
    <EditableList
      items={items}
      onChange={onChange}
      itemLabel={(item) => item.label}
      columns={[
        { label: "Icon", width: "200px" },
        { label: textLabel, width: "minmax(0, 1fr)" },
      ]}
      renderCells={(item, update) => (
        <>
          <IconPicker
            ariaLabel={`Icon ${item.label}`}
            value={item.icon}
            onChange={(value) => update({ icon: value })}
          />
          <TextInput
            aria-label={textLabel}
            value={item.label}
            onChange={(e) => update({ label: e.currentTarget.value })}
          />
        </>
      )}
      newItem={() => ({ id: newId(), icon: null, label: "" })}
      addLabel={addLabel}
      max={max}
      minWidth={420}
    />
  </Box>
);
